import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Users, ClipboardList, Clock, CheckCircle2, AlertTriangle, Plus,
  Calendar, Target, Phone, FileText, TrendingUp, Activity,
  BarChart3, RefreshCw, ChevronRight, User, Loader2, Play, Pause,
  Eye, Headphones, Star, Filter, X, ExternalLink, MessageSquare, Search
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

const PRIORITY_COLORS = {
  low: 'bg-muted/500',
  medium: 'bg-blue-500',
  high: 'bg-amber-500',
  urgent: 'bg-red-500'
};

const STATUS_COLORS = {
  pending: 'bg-muted/500',
  in_progress: 'bg-blue-500',
  completed: 'bg-emerald-500',
  overdue: 'bg-red-500'
};

const TASK_TYPE_ICONS = {
  review_calls: Phone,
  make_qa_notes: FileText,
  coaching_session: Users,
  review_homework: ClipboardList,
  general: Target
};

export default function QATaskManagement() {
  const { user, tenant } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tasks, setTasks] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [myAssignedUsers, setMyAssignedUsers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [leadDashboard, setLeadDashboard] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditors, setAuditors] = useState([]);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(60); // seconds
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Dialog states
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedAuditor, setSelectedAuditor] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    task_type: 'review_calls',
    frequency: 'daily',
    priority: 'medium',
    target_calls_to_review: 5,
    target_notes_to_make: 3,
    due_date: ''
  });
  
  // User detail view state
  const [dateFilter, setDateFilter] = useState('');
  const [selectedUserForReview, setSelectedUserForReview] = useState(null);
  const [userCalls, setUserCalls] = useState([]);
  const [userCallsLoading, setUserCallsLoading] = useState(false);
  const [userDetailDialogOpen, setUserDetailDialogOpen] = useState(false);
  const [callsFilter, setCallsFilter] = useState({ classification: '', qa_reviewed: '' });
  
  // Auditor detail view state
  const [selectedAuditorForView, setSelectedAuditorForView] = useState(null);
  const [auditorDetailDialogOpen, setAuditorDetailDialogOpen] = useState(false);
  const [auditorAssignedUsers, setAuditorAssignedUsers] = useState([]);
  const [auditorAssignedUsersLoading, setAuditorAssignedUsersLoading] = useState(false);
  
  // Assign users search
  const [assignUserSearch, setAssignUserSearch] = useState('');

  const isDark = theme === 'dark';
  // Lead/Admin can: create tasks, assign users to auditors, view all team performance
  // - Tenant owners (role === 'owner')
  // - Users with is_admin flag
  // - Lead Quality Auditors (role === 'lead_quality_auditor')
  const isLeadOrAdmin = user?.role === 'owner' || user?.role === 'admin' || 
                        user?.is_admin === true || user?.role === 'lead_quality_auditor';
  // QA Auditors can: view their own tasks, see assigned users, track their own progress
  const isQA = user?.role === 'quality_auditor' || user?.role === 'lead_quality_auditor';

  useEffect(() => {
    if (tenant?.id) {
      loadData();
    }
  }, [tenant?.id, dateFilter]);
  
  // Auto-refresh effect
  useEffect(() => {
    let intervalId;
    if (autoRefresh && tenant?.id) {
      intervalId = setInterval(() => {
        loadData(true); // silent refresh
        setLastRefresh(new Date());
      }, refreshInterval * 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, refreshInterval, tenant?.id, dateFilter]);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [tasksRes, usersRes] = await Promise.all([
        api.get(`/qa-tasks/tasks?tenant_id=${tenant.id}`),
        api.get('/users')
      ]);
      
      setTasks(tasksRes.data.tasks || []);
      setUsers(usersRes.data || []);
      
      // Load role-specific data
      if (isLeadOrAdmin) {
        const [assignRes, leadDashRes, heatmapRes] = await Promise.all([
          api.get(`/qa-tasks/assignments?tenant_id=${tenant.id}`),
          api.get(`/qa-tasks/dashboard/lead?tenant_id=${tenant.id}`),
          api.get(`/qa-tasks/heatmap/team?tenant_id=${tenant.id}`)
        ]);
        setAssignments(assignRes.data.assignments || []);
        setLeadDashboard(leadDashRes.data);
        setHeatmapData(heatmapRes.data.data || []);
        setAuditors(leadDashRes.data.auditors || []);
      }
      
      if (isQA && user?.id) {
        const dateParam = dateFilter ? `&date_filter=${dateFilter}` : '';
        const [dashRes, myUsersRes, heatmapRes] = await Promise.all([
          api.get(`/qa-tasks/dashboard/auditor?tenant_id=${tenant.id}&auditor_id=${user.id}`),
          api.get(`/qa-tasks/assignments/my-users?tenant_id=${tenant.id}&auditor_id=${user.id}${dateParam}`),
          api.get(`/qa-tasks/heatmap/auditor?tenant_id=${tenant.id}&auditor_id=${user.id}`)
        ]);
        setDashboard(dashRes.data);
        setMyAssignedUsers(myUsersRes.data.users || []);
        setHeatmapData(heatmapRes.data.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load task management data');
    } finally {
      setLoading(false);
    }
  };
  
  // Load calls for a specific user
  const loadUserCalls = async (userId, filter = {}) => {
    setUserCallsLoading(true);
    try {
      let url = `/qa-tasks/assignments/user-calls/${userId}?tenant_id=${tenant.id}`;
      if (dateFilter) url += `&date_filter=${dateFilter}`;
      if (filter.classification) url += `&classification=${filter.classification}`;
      // Only append qa_reviewed if it's explicitly true or false (not undefined, null, or empty string)
      if (filter.qa_reviewed === true || filter.qa_reviewed === false) {
        url += `&qa_reviewed=${filter.qa_reviewed}`;
      }
      
      const res = await api.get(url);
      setUserCalls(res.data.calls || []);
    } catch (error) {
      console.error('Error loading user calls:', error);
      toast.error('Failed to load calls');
    } finally {
      setUserCallsLoading(false);
    }
  };
  
  // Open user detail dialog
  const openUserDetail = async (user) => {
    setSelectedUserForReview(user);
    setCallsFilter({ classification: '', qa_reviewed: '' });
    setUserDetailDialogOpen(true);
    await loadUserCalls(user.id);
  };
  
  // Load assigned users for a specific auditor
  const loadAuditorAssignedUsers = async (auditorId) => {
    setAuditorAssignedUsersLoading(true);
    try {
      const res = await api.get(`/qa-tasks/assignments/my-users?tenant_id=${tenant.id}&auditor_id=${auditorId}`);
      setAuditorAssignedUsers(res.data.users || []);
    } catch (error) {
      console.error('Error loading auditor assigned users:', error);
      toast.error('Failed to load assigned users');
    } finally {
      setAuditorAssignedUsersLoading(false);
    }
  };
  
  // Open auditor detail dialog
  const openAuditorDetail = async (auditor) => {
    setSelectedAuditorForView(auditor);
    setAuditorDetailDialogOpen(true);
    await loadAuditorAssignedUsers(auditor.auditor_id);
  };
  
  // Filter users for assignment search
  const filteredUsersForAssign = users.filter(u => {
    if (!assignUserSearch) return true;
    const query = assignUserSearch.toLowerCase();
    return (
      u.name?.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query) ||
      u.phone_number?.includes(query) ||
      u.designation?.toLowerCase().includes(query)
    );
  });

  const createTask = async () => {
    if (!newTask.title || !selectedAuditor) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      await api.post('/qa-tasks/tasks', {
        ...newTask,
        tenant_id: tenant.id,
        auditor_id: selectedAuditor,
        assigned_by: user.id,
        due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null
      });
      
      toast.success('Task created successfully');
      setTaskDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        task_type: 'review_calls',
        frequency: 'daily',
        priority: 'medium',
        target_calls_to_review: 5,
        target_notes_to_make: 3,
        due_date: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const createAssignment = async () => {
    if (!selectedAuditor || selectedUsers.length === 0) {
      toast.error('Please select an auditor and at least one user');
      return;
    }
    
    try {
      await api.post('/qa-tasks/assignments', {
        tenant_id: tenant.id,
        auditor_id: selectedAuditor,
        assigned_user_ids: selectedUsers,
        assigned_by: user.id
      });
      
      toast.success('Users assigned successfully');
      setAssignmentDialogOpen(false);
      setSelectedAuditor(null);
      setSelectedUsers([]);
      loadData();
    } catch (error) {
      toast.error('Failed to create assignment');
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await api.put(`/qa-tasks/tasks/${taskId}`, { status });
      toast.success('Task status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const renderHeatmap = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const maxCount = Math.max(...heatmapData.map(d => d.count), 1);
    
    const getColor = (count) => {
      if (count === 0) return isDark ? 'bg-slate-800' : 'bg-muted';
      const intensity = count / maxCount;
      if (intensity > 0.75) return 'bg-emerald-500';
      if (intensity > 0.5) return 'bg-emerald-400';
      if (intensity > 0.25) return 'bg-emerald-300';
      return 'bg-emerald-200';
    };
    
    const getCount = (day, hour) => {
      const item = heatmapData.find(d => d.day === day && d.hour === hour);
      return item?.count || 0;
    };
    
    return (
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          <div className="flex gap-1 mb-2">
            <div className="w-12"></div>
            {hours.map(h => (
              <div key={h} className="w-6 text-center text-xs text-muted-foreground">
                {h}
              </div>
            ))}
          </div>
          {days.map(day => (
            <div key={day} className="flex gap-1 mb-1">
              <div className="w-12 text-xs text-muted-foreground flex items-center">{day}</div>
              {hours.map(hour => {
                const count = getCount(day, hour);
                return (
                  <div
                    key={`${day}-${hour}`}
                    className={cn(
                      "w-6 h-6 rounded-sm cursor-pointer transition-all hover:ring-2 hover:ring-offset-1",
                      getColor(count)
                    )}
                    title={`${day} ${hour}:00 - ${count} activities`}
                  />
                );
              })}
            </div>
          ))}
          <div className="flex items-center gap-2 mt-4 justify-end text-xs text-muted-foreground">
            <span>Less</span>
            <div className={cn("w-4 h-4 rounded-sm", isDark ? "bg-slate-800" : "bg-muted")} />
            <div className="w-4 h-4 rounded-sm bg-emerald-200" />
            <div className="w-4 h-4 rounded-sm bg-emerald-300" />
            <div className="w-4 h-4 rounded-sm bg-emerald-400" />
            <div className="w-4 h-4 rounded-sm bg-emerald-500" />
            <span>More</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qa-task-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">QA Task Management</h1>
          <p className="text-muted-foreground">
            {isLeadOrAdmin ? 'Manage auditor assignments and track progress' : 'Your assigned tasks and user reviews'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-Refresh Controls */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-background">
            <Button
              variant={autoRefresh ? "default" : "ghost"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={cn("h-7 px-2", autoRefresh && "bg-emerald-600 hover:bg-emerald-700")}
              data-testid="auto-refresh-toggle"
            >
              <RefreshCw className={cn("h-3.5 w-3.5 mr-1", autoRefresh && "animate-spin")} />
              {autoRefresh ? "On" : "Auto"}
            </Button>
            {autoRefresh && (
              <Select value={String(refreshInterval)} onValueChange={(v) => setRefreshInterval(Number(v))}>
                <SelectTrigger className="h-7 w-[70px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30s</SelectItem>
                  <SelectItem value="60">1m</SelectItem>
                  <SelectItem value="120">2m</SelectItem>
                  <SelectItem value="300">5m</SelectItem>
                </SelectContent>
              </Select>
            )}
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {lastRefresh.toLocaleTimeString()}
            </span>
          </div>
          
          <Button variant="outline" onClick={() => loadData()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {isLeadOrAdmin && (
            <>
              <Dialog open={assignmentDialogOpen} onOpenChange={(open) => {
                setAssignmentDialogOpen(open);
                if (!open) setAssignUserSearch('');
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Users className="h-4 w-4 mr-2" />
                    Assign Users
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Assign Users to Auditor</DialogTitle>
                    <DialogDescription>
                      Select a QA auditor and the users they should review
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <label className="text-sm font-medium">Quality Auditor</label>
                      <Select value={selectedAuditor || ''} onValueChange={setSelectedAuditor}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select auditor..." />
                        </SelectTrigger>
                        <SelectContent>
                          {auditors.map(a => (
                            <SelectItem key={a.auditor_id} value={a.auditor_id}>
                              {a.name} ({a.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium">Users to Review ({selectedUsers.length} selected)</label>
                        {selectedUsers.length > 0 && (
                          <Button variant="link" size="sm" className="text-xs h-auto p-0" onClick={() => setSelectedUsers([])}>
                            Clear all
                          </Button>
                        )}
                      </div>
                      {/* Search bar for users */}
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by name, email, phone, designation..."
                          value={assignUserSearch}
                          onChange={(e) => setAssignUserSearch(e.target.value)}
                          className="pl-9"
                        />
                        {assignUserSearch && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                            onClick={() => setAssignUserSearch('')}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <div className="max-h-60 overflow-y-auto border rounded-md">
                        {filteredUsersForAssign.length === 0 ? (
                          <p className="text-center text-muted-foreground py-8 text-sm">
                            No users match your search
                          </p>
                        ) : (
                          filteredUsersForAssign.map(u => (
                            <div
                              key={u.id || u._id}
                              className={cn(
                                "flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 border-b last:border-0",
                                selectedUsers.includes(u.id || u._id) && "bg-blue-500/10"
                              )}
                              onClick={() => {
                                const userId = u.id || u._id;
                                setSelectedUsers(prev => 
                                  prev.includes(userId) 
                                    ? prev.filter(id => id !== userId)
                                    : [...prev, userId]
                                );
                              }}
                            >
                              <input 
                                type="checkbox" 
                                checked={selectedUsers.includes(u.id || u._id)}
                                readOnly
                              />
                              <Avatar className="h-8 w-8">
                                <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{u.name}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {u.designation} {u.phone_number && `• ${u.phone_number}`}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Showing {filteredUsersForAssign.length} of {users.length} users
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAssignmentDialogOpen(false)}>Cancel</Button>
                    <Button onClick={createAssignment} disabled={!selectedAuditor || selectedUsers.length === 0}>
                      Assign {selectedUsers.length} User{selectedUsers.length !== 1 ? 's' : ''}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    New Task
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Create Task</DialogTitle>
                    <DialogDescription>Assign a task to a Quality Auditor</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <label className="text-sm font-medium">Assign To</label>
                      <Select value={selectedAuditor || ''} onValueChange={setSelectedAuditor}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select auditor..." />
                        </SelectTrigger>
                        <SelectContent>
                          {auditors.map(a => (
                            <SelectItem key={a.auditor_id} value={a.auditor_id}>
                              {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Title</label>
                      <Input
                        value={newTask.title}
                        onChange={e => setNewTask({...newTask, title: e.target.value})}
                        placeholder="Task title..."
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Description</label>
                      <Textarea
                        value={newTask.description}
                        onChange={e => setNewTask({...newTask, description: e.target.value})}
                        placeholder="Task description..."
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Type</label>
                        <Select 
                          value={newTask.task_type} 
                          onValueChange={v => setNewTask({...newTask, task_type: v})}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="review_calls">Review Calls</SelectItem>
                            <SelectItem value="make_qa_notes">Make QA Notes</SelectItem>
                            <SelectItem value="coaching_session">Coaching Session</SelectItem>
                            <SelectItem value="review_homework">Review Homework</SelectItem>
                            <SelectItem value="general">General</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Frequency</label>
                        <Select 
                          value={newTask.frequency} 
                          onValueChange={v => setNewTask({...newTask, frequency: v})}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="one_time">One Time</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Target Calls</label>
                        <Input
                          type="number"
                          value={newTask.target_calls_to_review}
                          onChange={e => setNewTask({...newTask, target_calls_to_review: parseInt(e.target.value)})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Target Notes</label>
                        <Input
                          type="number"
                          value={newTask.target_notes_to_make}
                          onChange={e => setNewTask({...newTask, target_notes_to_make: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Priority</label>
                        <Select 
                          value={newTask.priority} 
                          onValueChange={v => setNewTask({...newTask, priority: v})}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Due Date</label>
                        <Input
                          type="date"
                          value={newTask.due_date}
                          onChange={e => setNewTask({...newTask, due_date: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
                    <Button onClick={createTask}>Create Task</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Dashboard Cards */}
      {isQA && dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => setActiveTab('my-users')}
            data-testid="card-assigned-users"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Assigned Users</p>
                  <p className="text-3xl font-bold">{dashboard.assigned_users}</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-xl">
                  <Users className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => setActiveTab('tasks')}
            data-testid="card-pending-tasks"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Tasks</p>
                  <p className="text-3xl font-bold">{dashboard.pending_tasks}</p>
                  {dashboard.overdue_tasks > 0 && (
                    <p className="text-xs text-red-500">{dashboard.overdue_tasks} overdue</p>
                  )}
                </div>
                <div className="p-3 bg-amber-500/20 rounded-xl">
                  <ClipboardList className="h-6 w-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => navigate('/dashboard/calls')}
            data-testid="card-this-week-calls"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">This Week</p>
                  <p className="text-3xl font-bold">{dashboard.this_week?.calls_reviewed || 0}</p>
                  <p className="text-xs text-muted-foreground">calls reviewed</p>
                </div>
                <div className="p-3 bg-emerald-500/20 rounded-xl">
                  <Phone className="h-6 w-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => setActiveTab('heatmap')}
            data-testid="card-time-spent"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Time Spent</p>
                  <p className="text-3xl font-bold">{dashboard.this_week?.time_hours || 0}h</p>
                  <p className="text-xs text-muted-foreground">this week</p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-xl">
                  <Clock className="h-6 w-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lead Dashboard Cards */}
      {isLeadOrAdmin && leadDashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => setActiveTab('auditors')}
            data-testid="card-qa-auditors"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">QA Auditors</p>
                  <p className="text-3xl font-bold">{leadDashboard.total_auditors}</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-xl">
                  <Users className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => setAssignmentDialogOpen(true)}
            data-testid="card-active-assignments"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Assignments</p>
                  <p className="text-3xl font-bold">{leadDashboard.total_assignments}</p>
                </div>
                <div className="p-3 bg-amber-500/20 rounded-xl">
                  <Target className="h-6 w-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => navigate('/dashboard/calls')}
            data-testid="card-team-calls-reviewed"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Team Calls Reviewed</p>
                  <p className="text-3xl font-bold">{leadDashboard.team_this_week?.total_calls_reviewed || 0}</p>
                  <p className="text-xs text-muted-foreground">this week</p>
                </div>
                <div className="p-3 bg-emerald-500/20 rounded-xl">
                  <Phone className="h-6 w-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all"
            onClick={() => setActiveTab('tasks')}
            data-testid="card-overdue-tasks"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue Tasks</p>
                  <p className="text-3xl font-bold">{leadDashboard.tasks?.overdue || 0}</p>
                </div>
                <div className="p-3 bg-red-500/20 rounded-xl">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50">
          <TabsTrigger value="dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="tasks">
            <ClipboardList className="h-4 w-4 mr-2" />
            Tasks
          </TabsTrigger>
          {isQA && (
            <TabsTrigger value="my-users">
              <Users className="h-4 w-4 mr-2" />
              My Users
            </TabsTrigger>
          )}
          {isLeadOrAdmin && (
            <TabsTrigger value="auditors">
              <User className="h-4 w-4 mr-2" />
              Auditors
            </TabsTrigger>
          )}
          <TabsTrigger value="heatmap">
            <Activity className="h-4 w-4 mr-2" />
            Activity
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Recent Activity Chart */}
          {dashboard?.recent_activity && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity (Last 7 Days)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={dashboard.recent_activity.slice().reverse()}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: isDark ? '#e2e8f0' : '#1e293b', fontSize: 11 }}
                      tickFormatter={d => new Date(d).toLocaleDateString('en', { weekday: 'short' })}
                    />
                    <YAxis tick={{ fill: isDark ? '#e2e8f0' : '#1e293b', fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDark ? '#1e293b' : '#fff',
                        border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="calls_reviewed" fill="#3b82f6" name="Calls Reviewed" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="notes_made" fill="#10b981" name="Notes Made" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Lead: Auditor Performance */}
          {isLeadOrAdmin && leadDashboard?.auditors && (
            <Card>
              <CardHeader>
                <CardTitle>Auditor Performance (This Week)</CardTitle>
                <CardDescription>Click on numbers to view details</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Auditor</TableHead>
                      <TableHead className="text-right">Assigned Users</TableHead>
                      <TableHead className="text-right">Calls Reviewed</TableHead>
                      <TableHead className="text-right">Notes Made</TableHead>
                      <TableHead className="text-right">Time (hrs)</TableHead>
                      <TableHead className="text-right">Pending Tasks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leadDashboard.auditors.map(a => (
                      <TableRow key={a.auditor_id} className="hover:bg-muted/50">
                        <TableCell>
                          <div 
                            className="flex items-center gap-2 cursor-pointer hover:text-blue-500"
                            onClick={() => openAuditorDetail(a)}
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarFallback>{a.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{a.name}</p>
                              <p className="text-xs text-muted-foreground">{a.role}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="font-semibold text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openAuditorDetail(a)}
                          >
                            {a.assigned_users}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="font-semibold text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => {
                              setSelectedAuditorForView(a);
                              navigate('/dashboard/calls');
                            }}
                          >
                            {a.week_calls_reviewed}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="font-semibold text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                            onClick={() => openAuditorDetail(a)}
                          >
                            {a.week_notes_made}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{a.week_time_hours}h</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                              "font-semibold",
                              a.pending_tasks > 0 
                                ? "text-red-500 hover:text-red-700 hover:bg-red-50" 
                                : "text-muted-foreground"
                            )}
                            onClick={() => setActiveTab('tasks')}
                          >
                            <Badge variant={a.pending_tasks > 0 ? "destructive" : "secondary"}>
                              {a.pending_tasks}
                            </Badge>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Tasks</CardTitle>
              <CardDescription>
                {isLeadOrAdmin ? 'All tasks across QA team' : 'Your assigned tasks'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tasks.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No tasks found</p>
                ) : (
                  tasks.map(task => {
                    const TaskIcon = TASK_TYPE_ICONS[task.task_type] || Target;
                    const progress = task.target_calls_to_review > 0 
                      ? Math.round((task.calls_reviewed / task.target_calls_to_review) * 100)
                      : 0;
                    
                    return (
                      <div 
                        key={task.id}
                        className={cn(
                          "p-4 rounded-lg border transition-all hover:shadow-md",
                          isDark ? "bg-slate-800/50 border-slate-700" : "bg-card border-border",
                          task.status === 'overdue' && "border-red-500/50"
                        )}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <div className={cn(
                              "p-2 rounded-lg",
                              isDark ? "bg-slate-700" : "bg-muted"
                            )}>
                              <TaskIcon className="h-5 w-5 text-muted-foreground" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">{task.title}</h4>
                                <Badge className={PRIORITY_COLORS[task.priority]} variant="secondary">
                                  {task.priority}
                                </Badge>
                                <Badge className={STATUS_COLORS[task.status]}>
                                  {task.status.replace('_', ' ')}
                                </Badge>
                              </div>
                              {task.description && (
                                <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span>Assigned to: {task.auditor_name}</span>
                                <span>•</span>
                                <span className="capitalize">{task.frequency}</span>
                                {task.due_date && (
                                  <>
                                    <span>•</span>
                                    <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                                  </>
                                )}
                              </div>
                              {task.target_calls_to_review > 0 && (
                                <div className="mt-3">
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <span>Progress: {task.calls_reviewed}/{task.target_calls_to_review} calls</span>
                                    <span>{progress}%</span>
                                  </div>
                                  <Progress value={progress} className="h-2" />
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {task.status === 'pending' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => updateTaskStatus(task.id, 'in_progress')}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                            )}
                            {task.status === 'in_progress' && (
                              <Button 
                                size="sm"
                                onClick={() => updateTaskStatus(task.id, 'completed')}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Complete
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* My Users Tab (QA only) */}
        {isQA && (
          <TabsContent value="my-users" className="space-y-6">
            {/* Date Filter */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filter by:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: '', label: 'All Time' },
                  { value: 'today', label: 'Today' },
                  { value: 'yesterday', label: 'Yesterday' },
                  { value: 'this_week', label: 'This Week' },
                  { value: 'last_week', label: 'Last Week' },
                  { value: 'this_month', label: 'This Month' },
                  { value: 'last_month', label: 'Last Month' }
                ].map(filter => (
                  <Button
                    key={filter.value}
                    variant={dateFilter === filter.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDateFilter(filter.value)}
                    className={cn(
                      "text-xs",
                      dateFilter === filter.value && "bg-blue-600 hover:bg-blue-700"
                    )}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
            </div>
            
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Assigned Users to Review</CardTitle>
                    <CardDescription>
                      {dateFilter ? `Showing data for ${dateFilter.replace('_', ' ')}` : 'Focus on reviewing calls from these users'}
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="text-sm">
                    {myAssignedUsers.length} Users
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myAssignedUsers.length === 0 ? (
                    <p className="col-span-full text-center text-muted-foreground py-8">
                      No users assigned yet
                    </p>
                  ) : (
                    myAssignedUsers.map(u => (
                      <div 
                        key={u.id}
                        className={cn(
                          "p-4 rounded-lg border transition-all hover:shadow-lg",
                          isDark ? "bg-slate-800/50 border-slate-700" : "bg-card border-border"
                        )}
                      >
                        {/* User Header */}
                        <div 
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => openUserDetail(u)}
                        >
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={u.avatar_url} />
                            <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h4 className="font-medium hover:text-blue-500 transition-colors">{u.name}</h4>
                            <p className="text-xs text-muted-foreground">{u.designation}</p>
                            {u.email && <p className="text-xs text-muted-foreground">{u.email}</p>}
                          </div>
                        </div>
                        
                        {/* Stats Grid - Clickable */}
                        <div className="grid grid-cols-3 gap-2 mt-4">
                          <div 
                            className="text-center p-2 rounded-lg cursor-pointer hover:bg-blue-500/10 transition-colors"
                            onClick={() => {
                              setSelectedUserForReview(u);
                              setCallsFilter({ classification: '', qa_reviewed: '' });
                              setUserDetailDialogOpen(true);
                              loadUserCalls(u.id);
                            }}
                            title="View all calls"
                          >
                            <p className="text-lg font-bold text-blue-500">{u.total_calls}</p>
                            <p className="text-xs text-muted-foreground">Calls</p>
                          </div>
                          <div 
                            className="text-center p-2 rounded-lg cursor-pointer hover:bg-emerald-500/10 transition-colors"
                            onClick={() => {
                              setSelectedUserForReview(u);
                              setCallsFilter({ classification: '', qa_reviewed: 'true' });
                              setUserDetailDialogOpen(true);
                              loadUserCalls(u.id, { qa_reviewed: true });
                            }}
                            title="View reviewed calls"
                          >
                            <p className={cn(
                              "text-lg font-bold",
                              u.avg_score >= 8 ? "text-emerald-500" : 
                              u.avg_score >= 6 ? "text-blue-500" : 
                              u.avg_score >= 4 ? "text-amber-500" : "text-red-500"
                            )}>
                              {u.avg_score || '-'}
                            </p>
                            <p className="text-xs text-muted-foreground">Avg Score</p>
                          </div>
                          <div 
                            className="text-center p-2 rounded-lg cursor-pointer hover:bg-amber-500/10 transition-colors"
                            onClick={() => {
                              setSelectedUserForReview(u);
                              setCallsFilter({ classification: '', qa_reviewed: 'false' });
                              setUserDetailDialogOpen(true);
                              loadUserCalls(u.id, { qa_reviewed: false });
                            }}
                            title="View pending reviews"
                          >
                            <p className="text-lg font-bold text-amber-500">{u.pending_review}</p>
                            <p className="text-xs text-muted-foreground">To Review</p>
                          </div>
                        </div>
                        
                        {/* Classification Breakdown */}
                        {(u.best_calls > 0 || u.good_calls > 0 || u.improvement_calls > 0 || u.high_alert_calls > 0) && (
                          <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                            {u.best_calls > 0 && (
                              <Badge 
                                className="bg-emerald-500/20 text-emerald-600 hover:bg-emerald-500/30 cursor-pointer text-xs"
                                onClick={() => {
                                  setSelectedUserForReview(u);
                                  setCallsFilter({ classification: 'best', qa_reviewed: '' });
                                  setUserDetailDialogOpen(true);
                                  loadUserCalls(u.id, { classification: 'best' });
                                }}
                              >
                                {u.best_calls} Best
                              </Badge>
                            )}
                            {u.good_calls > 0 && (
                              <Badge 
                                className="bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 cursor-pointer text-xs"
                                onClick={() => {
                                  setSelectedUserForReview(u);
                                  setCallsFilter({ classification: 'good', qa_reviewed: '' });
                                  setUserDetailDialogOpen(true);
                                  loadUserCalls(u.id, { classification: 'good' });
                                }}
                              >
                                {u.good_calls} Good
                              </Badge>
                            )}
                            {u.improvement_calls > 0 && (
                              <Badge 
                                className="bg-amber-500/20 text-amber-600 hover:bg-amber-500/30 cursor-pointer text-xs"
                                onClick={() => {
                                  setSelectedUserForReview(u);
                                  setCallsFilter({ classification: 'improvement', qa_reviewed: '' });
                                  setUserDetailDialogOpen(true);
                                  loadUserCalls(u.id, { classification: 'improvement' });
                                }}
                              >
                                {u.improvement_calls} Improve
                              </Badge>
                            )}
                            {u.high_alert_calls > 0 && (
                              <Badge 
                                className="bg-red-500/20 text-red-600 hover:bg-red-500/30 cursor-pointer text-xs"
                                onClick={() => {
                                  setSelectedUserForReview(u);
                                  setCallsFilter({ classification: 'high_alert', qa_reviewed: '' });
                                  setUserDetailDialogOpen(true);
                                  loadUserCalls(u.id, { classification: 'high_alert' });
                                }}
                              >
                                {u.high_alert_calls} Alert
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-3">
                          <Button 
                            variant="outline" 
                            className="flex-1" 
                            size="sm"
                            onClick={() => openUserDetail(u)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Calls
                          </Button>
                          <Button 
                            variant="default" 
                            size="sm"
                            className="bg-amber-500 hover:bg-amber-600"
                            onClick={() => {
                              setSelectedUserForReview(u);
                              setCallsFilter({ classification: '', qa_reviewed: 'false' });
                              setUserDetailDialogOpen(true);
                              loadUserCalls(u.id, { qa_reviewed: false });
                            }}
                          >
                            <ClipboardList className="h-4 w-4 mr-1" />
                            Review ({u.pending_review})
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Auditors Tab (Lead only) */}
        {isLeadOrAdmin && (
          <TabsContent value="auditors" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Quality Auditors</CardTitle>
                <CardDescription>Manage auditor assignments and view performance. Click numbers to view details.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Auditor</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Assigned Users</TableHead>
                      <TableHead className="text-right">Pending Tasks</TableHead>
                      <TableHead className="text-right">This Week</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditors.map(a => (
                      <TableRow key={a.auditor_id} className="hover:bg-muted/50">
                        <TableCell>
                          <div 
                            className="flex items-center gap-2 cursor-pointer hover:text-blue-500"
                            onClick={() => openAuditorDetail(a)}
                          >
                            <Avatar>
                              <AvatarFallback>{a.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{a.name}</p>
                              <p className="text-xs text-muted-foreground">{a.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{a.role?.replace('_', ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="font-semibold text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => openAuditorDetail(a)}
                          >
                            {a.assigned_users}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                              "font-semibold",
                              a.pending_tasks > 3 
                                ? "text-red-500 hover:text-red-700 hover:bg-red-50" 
                                : "text-muted-foreground"
                            )}
                            onClick={() => setActiveTab('tasks')}
                          >
                            <Badge variant={a.pending_tasks > 3 ? "destructive" : "secondary"}>
                              {a.pending_tasks}
                            </Badge>
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sm hover:bg-muted"
                            onClick={() => openAuditorDetail(a)}
                          >
                            {a.week_calls_reviewed} calls, {a.week_notes_made} notes
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openAuditorDetail(a)}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Activity Heatmap Tab */}
        <TabsContent value="heatmap" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Activity Heatmap</CardTitle>
              <CardDescription>
                {isLeadOrAdmin ? 'Team activity by day and hour' : 'Your activity patterns'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderHeatmap()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Auditor Detail Dialog - View Assigned Users */}
      <Dialog open={auditorDetailDialogOpen} onOpenChange={setAuditorDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedAuditorForView && (
                  <>
                    <Avatar className="h-12 w-12">
                      <AvatarFallback>{selectedAuditorForView.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <DialogTitle className="text-xl">{selectedAuditorForView.name}</DialogTitle>
                      <DialogDescription>
                        {selectedAuditorForView.role?.replace('_', ' ')} • {selectedAuditorForView.email}
                      </DialogDescription>
                    </div>
                  </>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => setAuditorDetailDialogOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          
          {/* Auditor Stats Summary */}
          {selectedAuditorForView && (
            <div className="grid grid-cols-5 gap-3 py-3 border-y">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-500">{selectedAuditorForView.assigned_users}</p>
                <p className="text-xs text-muted-foreground">Assigned Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-500">{selectedAuditorForView.week_calls_reviewed || 0}</p>
                <p className="text-xs text-muted-foreground">Calls Reviewed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-500">{selectedAuditorForView.week_notes_made || 0}</p>
                <p className="text-xs text-muted-foreground">Notes Made</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-500">{selectedAuditorForView.week_time_hours || 0}h</p>
                <p className="text-xs text-muted-foreground">Time Spent</p>
              </div>
              <div className="text-center">
                <p className={cn(
                  "text-2xl font-bold",
                  selectedAuditorForView.pending_tasks > 0 ? "text-red-500" : "text-muted-foreground"
                )}>
                  {selectedAuditorForView.pending_tasks || 0}
                </p>
                <p className="text-xs text-muted-foreground">Pending Tasks</p>
              </div>
            </div>
          )}
          
          {/* Assigned Users List */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex items-center justify-between py-2">
              <h4 className="font-medium">Assigned Users</h4>
              <Badge variant="secondary">{auditorAssignedUsers.length} users</Badge>
            </div>
            
            {auditorAssignedUsersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : auditorAssignedUsers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No users assigned to this auditor</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {auditorAssignedUsers.map(u => (
                  <div
                    key={u.id}
                    className={cn(
                      "p-3 rounded-lg border transition-all hover:shadow-md cursor-pointer",
                      isDark ? "bg-slate-800/50 border-slate-700" : "bg-card border-border"
                    )}
                    onClick={() => {
                      setAuditorDetailDialogOpen(false);
                      openUserDetail(u);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={u.avatar_url} />
                        <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{u.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.designation || u.email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                      <div>
                        <p className="font-semibold text-blue-500">{u.total_calls}</p>
                        <p className="text-xs text-muted-foreground">Calls</p>
                      </div>
                      <div>
                        <p className={cn(
                          "font-semibold",
                          u.avg_score >= 8 ? "text-emerald-500" : 
                          u.avg_score >= 6 ? "text-blue-500" : 
                          u.avg_score >= 4 ? "text-amber-500" : "text-red-500"
                        )}>
                          {u.avg_score || '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">Avg Score</p>
                      </div>
                      <div>
                        <p className="font-semibold text-amber-500">{u.pending_review}</p>
                        <p className="text-xs text-muted-foreground">To Review</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex justify-end pt-3 border-t">
            <Button variant="outline" onClick={() => setAuditorDetailDialogOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* User Detail Dialog - View and Review Calls */}
      <Dialog open={userDetailDialogOpen} onOpenChange={setUserDetailDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedUserForReview && (
                  <>
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={selectedUserForReview.avatar_url} />
                      <AvatarFallback>{selectedUserForReview.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <DialogTitle className="text-xl">{selectedUserForReview.name}</DialogTitle>
                      <DialogDescription>
                        {selectedUserForReview.designation} • {selectedUserForReview.email}
                      </DialogDescription>
                    </div>
                  </>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={() => setUserDetailDialogOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          
          {/* Stats Summary */}
          {selectedUserForReview && (
            <div className="grid grid-cols-4 gap-3 py-3 border-y">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-500">{selectedUserForReview.total_calls}</p>
                <p className="text-xs text-muted-foreground">Total Calls</p>
              </div>
              <div className="text-center">
                <p className={cn(
                  "text-2xl font-bold",
                  selectedUserForReview.avg_score >= 8 ? "text-emerald-500" : 
                  selectedUserForReview.avg_score >= 6 ? "text-blue-500" : 
                  selectedUserForReview.avg_score >= 4 ? "text-amber-500" : "text-red-500"
                )}>
                  {selectedUserForReview.avg_score || '-'}
                </p>
                <p className="text-xs text-muted-foreground">Avg Score</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-500">{selectedUserForReview.reviewed_calls || 0}</p>
                <p className="text-xs text-muted-foreground">Reviewed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-500">{selectedUserForReview.pending_review}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
            </div>
          )}
          
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 py-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            <Select 
              value={callsFilter.classification || 'all'} 
              onValueChange={(v) => {
                const newFilter = { ...callsFilter, classification: v === 'all' ? '' : v };
                setCallsFilter(newFilter);
                if (selectedUserForReview) {
                  loadUserCalls(selectedUserForReview.id, newFilter);
                }
              }}
            >
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="Classification" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                <SelectItem value="best">Best</SelectItem>
                <SelectItem value="good">Good</SelectItem>
                <SelectItem value="improvement">Improvement</SelectItem>
                <SelectItem value="high_alert">High Alert</SelectItem>
              </SelectContent>
            </Select>
            
            <Select 
              value={callsFilter.qa_reviewed || 'all'} 
              onValueChange={(v) => {
                const newFilter = { ...callsFilter, qa_reviewed: v === 'all' ? '' : v };
                setCallsFilter(newFilter);
                if (selectedUserForReview) {
                  loadUserCalls(selectedUserForReview.id, { 
                    ...newFilter, 
                    qa_reviewed: v === 'all' ? undefined : v === 'true' 
                  });
                }
              }}
            >
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="Review Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="false">Pending Review</SelectItem>
                <SelectItem value="true">Reviewed</SelectItem>
              </SelectContent>
            </Select>
            
            <Badge variant="secondary" className="ml-auto">
              {userCalls.length} calls
            </Badge>
          </div>
          
          {/* Calls List */}
          <div className="flex-1 overflow-y-auto">
            {userCallsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : userCalls.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Phone className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No calls found for the selected filters</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Call</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Classification</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userCalls.map(call => (
                    <TableRow key={call.id} className="hover:bg-muted/50">
                      <TableCell>
                        <div className="max-w-[200px]">
                          <p className="font-medium truncate" title={call.filename}>
                            {call.filename}
                          </p>
                          {call.call_type && (
                            <p className="text-xs text-muted-foreground capitalize">{call.call_type}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">
                          {call.created_at ? format(new Date(call.created_at), 'MMM d, yyyy') : '-'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {call.created_at ? format(new Date(call.created_at), 'h:mm a') : ''}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">
                            {call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : '-'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className={cn(
                            "h-4 w-4",
                            call.average_score >= 8 ? "text-emerald-500" : 
                            call.average_score >= 6 ? "text-blue-500" : 
                            call.average_score >= 4 ? "text-amber-500" : "text-red-500"
                          )} />
                          <span className="font-medium">
                            {call.average_score?.toFixed(1) || 'N/A'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn(
                          "capitalize",
                          call.classification === 'best' ? "bg-emerald-500" :
                          call.classification === 'good' ? "bg-blue-500" :
                          call.classification === 'improvement' ? "bg-amber-500" :
                          call.classification === 'high_alert' ? "bg-red-500" : "bg-muted/500"
                        )}>
                          {call.classification?.replace('_', ' ') || 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {call.qa_reviewed ? (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Reviewed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/dashboard/call/${call.id}`)}
                            title="View call details, listen to audio, collaborate"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setUserDetailDialogOpen(false);
                              navigate(`/dashboard/call/${call.id}`);
                            }}
                            title="Open in new tab"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-3 border-t">
            <p className="text-sm text-muted-foreground">
              Click on any call to view details, listen to audio, add QA notes, and collaborate
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setUserDetailDialogOpen(false)}>
                Close
              </Button>
              {selectedUserForReview?.pending_review > 0 && (
                <Button 
                  className="bg-amber-500 hover:bg-amber-600"
                  onClick={() => {
                    // Navigate to the first pending call
                    const pendingCall = userCalls.find(c => !c.qa_reviewed);
                    if (pendingCall) {
                      setUserDetailDialogOpen(false);
                      navigate(`/dashboard/call/${pendingCall.id}`);
                    }
                  }}
                >
                  <Play className="h-4 w-4 mr-1" />
                  Start Reviewing
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
